import React, { useState, useEffect } from 'react';
import { triggerNotificationPrompt } from '../components/NotificationOnboarding';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  CheckCircle2, Clock, AlertCircle, Users, Calendar, 
  CreditCard, UserPlus, FileText, TrendingUp, DollarSign 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { performanceAPI, tasksAPI, teamAPI, reportsAPI } from '../services/api';
import apiClient from '../services/api';

const ClientDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchDashboardData();
    triggerNotificationPrompt();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [performanceRes, tasksRes, teamRes] = await Promise.all([
        performanceAPI.getOverview(),
        tasksAPI.getAll(),
        teamAPI.getAll()
      ]);

      // Fetch actual billing data for the client
      let totalSpent = 0;
      try {
        const billingRes = await apiClient.get('/payments/client-spending');
        totalSpent = billingRes.data?.total_spent || 0;
      } catch (e) {
        totalSpent = 0;
      }

      // Fetch subscription status
      try {
        const subRes = await apiClient.get('/payments/current-subscription');
        if (subRes.data && subRes.data.plan) {
          setSubscription({
            plan: subRes.data.plan_name || subRes.data.plan,
            nextBillingDate: subRes.data.next_billing_date || 'N/A',
            status: subRes.data.status || 'active'
          });
        }
      } catch (e) {
        setSubscription(null);
      }

      setStats({
        completedTasks: performanceRes.data.completedTasks || 0,
        inProgressTasks: performanceRes.data.inProgressTasks || 0,
        pendingTasks: performanceRes.data.pendingTasks || 0,
        activeTeam: teamRes.data.filter(m => m.status === 'active').length || 0,
        totalSpent: totalSpent
      });

      setRecentTasks(tasksRes.data.slice(0, 4));
      setTeamMembers(teamRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
    <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  const statsArray = [
    {
      title: 'Active VAs',
      value: stats?.activeTeam || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Active Tasks',
      value: stats?.inProgressTasks || 0,
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'Completed',
      value: stats?.completedTasks || 0,
      icon: CheckCircle2,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Monthly Spend',
      value: `$${stats?.totalSpent?.toLocaleString() || '0'}`,
      icon: DollarSign,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0D0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Welcome back, {user.name || 'Client'}!
          </h1>
          <p className="text-[#4B5563] mt-1 text-sm">
            Here's an overview of your virtual assistant team
          </p>
        </div>
        <Link to="/hire">
          <Button className="bg-[#5FD068] hover:bg-[#4ec057] text-white" data-testid="hire-va-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Hire New VA
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {statsArray.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={`stat-card-${index}`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-xl sm:text-2xl font-medium tracking-tight text-[#0A0D0A] mt-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{stat.value}</p>
                  </div>
                  <div className={`${stat.bgColor} p-2 sm:p-3 rounded-lg`}>
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <Link to="/tasks">
              <Button variant="outline" className="w-full h-24 flex flex-col items-center justify-center gap-2" data-testid="quick-new-task-btn">
                <CheckCircle2 className="w-6 h-6 text-[#5FD068]" />
                <span className="text-sm">New Task</span>
              </Button>
            </Link>
            <Link to="/team">
              <Button variant="outline" className="w-full h-24 flex flex-col items-center justify-center gap-2" data-testid="quick-view-team-btn">
                <Users className="w-6 h-6 text-blue-500" />
                <span className="text-sm">View Team</span>
              </Button>
            </Link>
            <Link to="/billing">
              <Button variant="outline" className="w-full h-24 flex flex-col items-center justify-center gap-2" data-testid="quick-billing-btn">
                <CreditCard className="w-6 h-6 text-purple-500" />
                <span className="text-sm">Billing</span>
              </Button>
            </Link>
            <Link to="/reports">
              <Button variant="outline" className="w-full h-24 flex flex-col items-center justify-center gap-2" data-testid="quick-reports-btn">
                <FileText className="w-6 h-6 text-orange-500" />
                <span className="text-sm">Reports</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tasks */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Tasks</CardTitle>
            <Link to="/tasks" className="text-sm text-blue-500 hover:text-blue-600">
              View All
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentTasks.length > 0 ? recentTasks.map((task) => (
                <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">{task.title}</h4>
                    <p className="text-sm text-gray-600">{task.assigned_to}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ml-2 whitespace-nowrap ${
                    task.status === 'completed' ? 'bg-green-100 text-green-700' :
                    task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    'bg-orange-100 text-orange-700'
                  }`}>
                    {task.status.replace('_', ' ')}
                  </span>
                </div>
              )) : (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No tasks yet</p>
                  <Link to="/tasks">
                    <Button variant="outline" size="sm" className="mt-3">
                      Create First Task
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Your Team */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Your Team</CardTitle>
            <Link to="/team" className="text-sm text-blue-500 hover:text-blue-600">
              View All
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {teamMembers.length > 0 ? teamMembers.map((member) => (
                <Link key={member.id} to={`/va/${member.id}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid={`my-va-${member.id}`}>
                  <div className="flex items-center space-x-3">
                    <img
                      src={member.avatar}
                      alt={member.name}
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <h4 className="font-medium text-gray-900">{member.name}</h4>
                      <p className="text-sm text-gray-600">{member.role}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      member.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {member.status}
                    </span>
                  </div>
                </Link>
              )) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No team members yet</p>
                  <Link to="/hire">
                    <Button variant="outline" size="sm" className="mt-3">
                      Hire Your First VA
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subscription Status - Only show if client has a subscription */}
      {subscription && (
        <Card className="bg-gradient-to-r from-[#5FD068]/10 to-blue-500/10 border-none">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-900">{subscription.plan}</h3>
                <p className="text-sm text-[#4B5563] mt-1 text-sm">
                  You have access to all features. Next billing date: {subscription.nextBillingDate}
                </p>
              </div>
              <Link to="/billing">
                <Button variant="outline" data-testid="manage-subscription-btn">
                  Manage Subscription
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Subscription - Prompt to subscribe */}
      {!subscription && (
        <Card className="bg-gradient-to-r from-gray-100 to-gray-50 border-dashed border-2 border-gray-300">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-900">No Active Subscription</h3>
                <p className="text-sm text-[#4B5563] mt-1 text-sm">
                  Subscribe to a plan to hire VAs and access all features.
                </p>
              </div>
              <Link to="/billing">
                <Button className="bg-[#5FD068] hover:bg-[#4ec057]" data-testid="subscribe-btn">
                  View Plans
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientDashboard;
